import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import {
  availableHanziScript,
  useTraditionalCharactersEnabled,
} from "@-/dev.improve.haniverse.lib.posthog";

function firstScalarCharacter(grapheme: string): string {
  const codePoint = grapheme.codePointAt(0);
  return codePoint === undefined ? grapheme : String.fromCodePoint(codePoint);
}

/**
 * Query-options for grapheme primitive rank, bound to the active script and the given grapheme.
 * Reads from `grapheme_prc_data` (simplified) or `grapheme_tw_data` (traditional). A stored rank
 * of `0` reads back as `null`.
 */
export function useGetGraphemePrimitiveRankQueryKey() {
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const withTableChangeVersion = useActorTableChangeVersion("user", {});

  return (grapheme: string) =>
    withTableChangeVersion([
      "graphemePrimitiveRank",
      firstScalarCharacter(grapheme),
      { actorUserDataQueryOptions, staticDb, traditionalCharactersEnabled },
    ] as const);
}

export const [useGetGraphemePrimitiveRankQueryOptions, graphemePrimitiveRankQueryFn] =
  createUseQueryOptions(
    useGetGraphemePrimitiveRankQueryKey,
    async ({
      client,
      queryKey: [
        _,
        grapheme,
        { actorUserDataQueryOptions, staticDb, traditionalCharactersEnabled },
      ],
    }) => {
      const actorUserData = await client.fetchQuery(actorUserDataQueryOptions);
      const hanziScript = availableHanziScript(
        actorUserData.user.hanziScript,
        traditionalCharactersEnabled,
      );
      if (hanziScript !== "simplified" && hanziScript !== "traditional") {
        return null;
      }

      const db = staticDb(import.meta.location);
      const glyph = firstScalarCharacter(grapheme);

      const row = await db.query.grapheme.findFirst({
        columns: {},
        where: { glyph },
        with:
          hanziScript === "traditional"
            ? { twData: { columns: { rank: true } } }
            : { prcData: { columns: { rank: true } } },
      });

      const rank =
        hanziScript === "traditional" ? (row?.twData?.rank ?? null) : (row?.prcData?.rank ?? null);
      return rank === null || rank === 0 ? null : rank;
    },
  );
